import { useDeviceCapabilities } from '@invision/core';
import MUIToolTip, { TooltipProps } from '@mui/material/Tooltip';
import React from 'react';

const TOUCH_DELAY_MS = 6;
const TOUCH_LEAVE_DELAY_MS = 1500;

type PopperSlotProps = NonNullable<
  NonNullable<TooltipProps['slotProps']>['popper']
>;

/**
 * Accept legacy `PopperProps` (removed in MUI v9) and map them to
 * `slotProps.popper` so existing call sites keep working.
 */
export type InvTooltipProps = TooltipProps & {
  /** @deprecated Use `slotProps.popper` instead. */
  PopperProps?: PopperSlotProps;
};

/** Extension of [Material-UI Tooltip](https://material-ui.com/components/tooltips/) -  [Api](https://material-ui.com/api/tooltip/)
 *
 * Automatically handles devices without hover support using MUI's built-in touch support.
 * On devices without hover support (iPad, tablets), tooltips show on touch and hold (enterTouchDelay={6}ms).
 * On desktop devices with hover support, tooltips work with hover as usual.
 */
export const ToolTip = ({
  children,
  classes,
  enterTouchDelay,
  leaveTouchDelay,
  PopperProps,
  slotProps,
  ...props
}: InvTooltipProps) => {
  // Detect touch devices using device capabilities hook
  // Touch devices need touch delay for tooltips to work on touch and hold
  const { isTouchDevice } = useDeviceCapabilities();

  // Auto-configure for touch devices if not explicitly provided
  const touchDelay = isTouchDevice ? TOUCH_DELAY_MS : undefined;
  const touchLeaveDelay = isTouchDevice ? TOUCH_LEAVE_DELAY_MS : undefined;

  return (
    <MUIToolTip
      {...props}
      enterTouchDelay={enterTouchDelay ?? touchDelay}
      leaveTouchDelay={leaveTouchDelay ?? touchLeaveDelay}
      slotProps={{
        ...slotProps,
        popper: {
          ...(typeof PopperProps === 'object' ? PopperProps : {}),
          ...(typeof slotProps?.popper === 'object' ? slotProps.popper : {})
        } as PopperSlotProps,
        tooltip: {
          sx: {
            '&.MuiTooltip-tooltipPlacementBottom': {
              marginTop: '0.5rem',
              marginBottom: '0.5rem'
            },
            '&.MuiTooltip-tooltipPlacementTop': {
              marginTop: '0.5rem',
              marginBottom: '0.5rem'
            }
          },
          ...(typeof slotProps?.tooltip === 'object' ? slotProps.tooltip : {})
        }
      }}
      classes={classes}
    >
      {children}
    </MUIToolTip>
  );
};

export type { TooltipProps };
